// client/src/components/inventory/SyncButton.tsx

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, Wifi, WifiOff, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { indexedDBService } from "@/lib/db/indexed-db";
import {
  createProduct,
  createInventoryItem,
  updateInventoryItem,
  deleteProduct,
} from "@/lib/api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

interface SyncButtonProps {
  shopId: string;
  onSyncComplete?: () => void;
}

export function SyncButton({ shopId, onSyncComplete }: SyncButtonProps) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [isOnline, setIsOnline] = useState(false);
  const [isServerReachable, setIsServerReachable] = useState<boolean | null>(
    null,
  );

  // ============ VERIFICAR SI EL SERVIDOR ESTÁ ACCESIBLE ============
  const checkServer = async (): Promise<boolean> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);

      const response = await fetch("/api/health", {
        method: "HEAD",
        signal: controller.signal,
        cache: "no-cache",
      }).catch((error) => {
        return null;
      });

      clearTimeout(timeoutId);
      const reachable = response !== null && response.ok;

      // Actualizar estados
      setIsServerReachable(reachable);
      setIsOnline(reachable);

      return reachable;
    } catch (error) {
      setIsServerReachable(false);
      setIsOnline(false);
      return false;
    }
  };

  // ============ ESCUCHAR CAMBIOS EN LA CONEXIÓN DEL NAVEGADOR ============
  useEffect(() => {
    const handleOnline = async () => {
      console.log(
        "📡 [SyncButton] Navegador detectó conexión, verificando servidor...",
      );

      // Verificar servidor inmediatamente
      const reachable = await checkServer();

      if (reachable) {
        // Verificar si hay operaciones pendientes
        const operations = await indexedDBService.getPendingOperations();

        if (operations.length > 0) {
          console.log(
            `🔄 [SyncButton] Hay ${operations.length} operaciones pendientes para sincronizar`,
          );

          // Pequeño delay para asegurar que la conexión está estable
          setTimeout(() => {
            syncOperations();
          }, 1500);
        } else {
          console.log("✅ [SyncButton] No hay operaciones pendientes");
        }
      }
    };

    const handleOffline = () => {
      console.log("📡 [SyncButton] Navegador detectó desconexión");
      setIsOnline(false);
      setIsServerReachable(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // También verificar al montar el componente si ya hay conexión
    if (navigator.onLine) {
      handleOnline();
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []); // Dependencias vacías para que se ejecute solo una vez

  // ============ VERIFICACIÓN PERIÓDICA Y SINCRONIZACIÓN ============
  useEffect(() => {
    // Verificar inmediatamente al montar
    const initialCheck = async () => {
      await checkServer();
      const operations = await indexedDBService.getPendingOperations();
      if (operations.length > 0 && isOnline) {
        console.log(
          `🔄 [SyncButton] Hay ${operations.length} operaciones pendientes al iniciar`,
        );
        syncOperations();
      }
    };

    initialCheck();

    // Verificar cada 8 segundos (frecuencia balanceada)
    const interval = setInterval(async () => {
      const wasOffline = !isOnline;
      const reachable = await checkServer();

      // Siempre que el servidor esté reachable, verificar pendientes
      if (reachable) {
        const operations = await indexedDBService.getPendingOperations();

        if (operations.length > 0) {
          console.log(
            `🔄 [SyncButton] Verificación periódica: ${operations.length} operaciones pendientes`,
          );
          syncOperations();
        }

        // Si estábamos offline y ahora online, log adicional
        if (wasOffline) {
          console.log("📡 [SyncButton] Conexión restaurada");
        }
      }
    }, 8000); // 8 segundos es un buen balance

    return () => clearInterval(interval);
  }, [isOnline]); // Dependencia de isOnline para detectar cambios

  // ============ OBTENER CONTADOR DE OPERACIONES PENDIENTES ============
  useEffect(() => {
    const loadPendingCount = async () => {
      const operations = await indexedDBService.getPendingOperations();
      setPendingCount(operations.length);
    };

    loadPendingCount();

    const handlePendingChange = () => {
      loadPendingCount();
    };

    window.addEventListener("pending-changed", handlePendingChange);

    return () => {
      window.removeEventListener("pending-changed", handlePendingChange);
    };
  }, []);

  // ============ SINCRONIZACIÓN AUTOMÁTICA ============
  const autoSync = async () => {
    if (isSyncing) return;

    const operations = await indexedDBService.getPendingOperations();
    if (operations.length > 0) {
      console.log("🔄 Iniciando sincronización automática...");
      await syncOperations();
    }
  };

  // ============ SINCRONIZAR OPERACIONES PENDIENTES ============
  const syncOperations = async () => {
    if (isSyncing) {
      toast({
        title: "Sincronización en progreso",
        description: "Ya hay una sincronización en curso",
        duration: 3000,
      });
      return;
    }

    if (!isOnline) {
      toast({
        title: "Sin conexión",
        description: "No hay conexión al servidor para sincronizar",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    setIsSyncing(true);

    try {
      const operations = await indexedDBService.getPendingOperations();

      if (operations.length === 0) {
        toast({
          title: "Sin cambios pendientes",
          description: "Todos los datos están sincronizados",
          duration: 3000,
        });
        return;
      }

      console.log(
        `🔄 Sincronizando ${operations.length} operaciones pendientes...`,
      );
      console.log(
        "📋 Operaciones pendientes:",
        operations.map((op) => ({
          id: op.id,
          type: op.type,
          data: op.data,
        })),
      );

      let successCount = 0;
      let errorCount = 0;

      for (const op of operations) {
        try {
          console.log(`🔄 Procesando operación ${op.type}:`, op);

          if (op.type === "create" && op.entityType === "inventory") {
            // ============ CREAR PRODUCTO OFFLINE ============
            console.log("📦 Procesando operación CREATE:", {
              opId: op.id,
              opData: op.data,
            });

            try {
              // Extraer datos de la operación (puede venir en diferentes formatos)
              const productData = op.data.productData;
              const product = op.data.product;
              const inventory = op.data.inventory;

              // Verificar que tenemos al menos los datos mínimos
              if (!productData && !product) {
                throw new Error("Operación CREATE sin datos de producto");
              }

              let result;

              // SIEMPRE usar productData si existe (formato nuevo y completo)
              if (productData) {
                console.log("📦 Usando formato completo (productData):", {
                  name: productData.name,
                  category: productData.category,
                  price: productData.price,
                  stock: productData.stock,
                });

                result = await createProduct(shopId, {
                  name: productData.name,
                  category: productData.category,
                  price: productData.price,
                  stock: productData.stock,
                  threshold: productData.threshold,
                  supplierId: productData.supplierId,
                  supplierNotes: productData.supplierNotes,
                  acquisitionCost: productData.acquisitionCost,
                  profitMargin: productData.profitMargin,
                  batchNumber: productData.batchNumber,
                  purchaseDate: productData.purchaseDate,
                  expiryDate: productData.expiryDate,
                  minimumOrderQuantity: productData.minimumOrderQuantity,
                  leadTimeDays: productData.leadTimeDays,
                  dailySalesRate: productData.dailySalesRate,
                  reorderPoint: productData.reorderPoint,
                });

                console.log(
                  "✅ Producto creado en servidor con datos completos:",
                  result.product?.id || result.id,
                );
              }
              // Fallback a formato antiguo si no hay productData
              else if (product && inventory) {
                console.log(
                  "📦 Usando formato antiguo (product + inventory):",
                  {
                    name: product.name,
                    category: product.category,
                    price: inventory.price,
                    stock: inventory.stock,
                  },
                );

                result = await createProduct(shopId, {
                  name: product.name,
                  category: product.category,
                  price: inventory.price,
                  stock: inventory.stock,
                  threshold: inventory.lowStockThreshold,
                });

                console.log(
                  "✅ Producto creado en servidor (modo compatibilidad):",
                  result.product?.id || result.id,
                );
              } else {
                throw new Error("Formato de operación CREATE no reconocido");
              }

              // ===== AHORA SÍ, ELIMINAR DATOS TEMPORALES =====
              console.log("🗑️ Eliminando datos temporales de IndexedDB...");

              // Intentar eliminar producto temporal (si existe)
              if (product?.id) {
                try {
                  await indexedDBService.deleteProduct(product.id);
                  console.log(`✅ Producto temporal eliminado: ${product.id}`);
                } catch (deleteError) {
                  console.warn(
                    `⚠️ No se pudo eliminar producto ${product.id}:`,
                    deleteError,
                  );
                }
              }

              // Intentar eliminar inventario temporal (si existe)
              if (inventory?.id) {
                try {
                  await indexedDBService.deleteInventoryItem(inventory.id);
                  console.log(
                    `✅ Inventario temporal eliminado: ${inventory.id}`,
                  );
                } catch (deleteError) {
                  console.warn(
                    `⚠️ No se pudo eliminar inventario ${inventory.id}:`,
                    deleteError,
                  );
                }
              }

              // Finalmente, eliminar la operación pendiente
              await indexedDBService.removePendingOperation(op.id);
              console.log(`✅ Operación pendiente eliminada: ${op.id}`);

              successCount++;
              console.log(`🎉 Operación ${op.id} completada exitosamente`);
            } catch (error) {
              console.error(`❌ Error en operación CREATE ${op.id}:`, error);

              // Registrar el error detalladamente
              let errorMessage = "Error desconocido";
              let isNetworkError = false;

              if (error instanceof Error) {
                errorMessage = error.message;
                console.error("Stack:", error.stack);

                // Detectar errores de red para no marcarlos como errorCount
                if (
                  errorMessage.includes("Failed to fetch") ||
                  errorMessage.includes("NetworkError") ||
                  errorMessage.includes("Network request failed") ||
                  errorMessage.includes("load failed")
                ) {
                  isNetworkError = true;
                }
              }

              if (isNetworkError) {
                // Error de red: actualizar retryCount pero no incrementar errorCount
                console.log(
                  `📡 Error de red en operación ${op.id}, se reintentará después`,
                );
                await indexedDBService.updatePendingOperation(op.id, {
                  retryCount: (op.retryCount || 0) + 1,
                  lastError: errorMessage,
                  timestamp: new Date().toISOString(),
                });
              } else {
                // Error real: incrementar contador y guardar error
                errorCount++;
                await indexedDBService.updatePendingOperation(op.id, {
                  retryCount: (op.retryCount || 0) + 1,
                  lastError: errorMessage,
                  timestamp: new Date().toISOString(),
                });
              }
            }
          } else if (op.type === "update") {
            // ============ ACTUALIZAR PRODUCTO OFFLINE ============
            console.log("📝 Procesando operación UPDATE");

            try {
              await updateInventoryItem(op.data.id, op.data.updates);
              console.log("✅ Item actualizado en servidor");
            } catch (updateError) {
              console.log("⚠️ Error actualizando en servidor");
            }

            await indexedDBService.removePendingOperation(op.id);
            successCount++;
          } else if (op.type === "delete") {
            // ============ ELIMINAR PRODUCTO OFFLINE ============
            console.log("🗑️ Procesando operación DELETE");

            const isTemporal =
              op.data.productId?.toString().startsWith("temp_") ||
              op.data.id?.toString().startsWith("temp_");

            if (isTemporal) {
              console.log("🗑️ Producto temporal, eliminando solo de IndexedDB");

              await indexedDBService.deleteInventoryItem(op.data.id);
              await indexedDBService.deleteProduct(op.data.productId);
              await indexedDBService.removePendingOperation(op.id);

              successCount++;
            } else {
              try {
                await deleteProduct(op.data.productId);
                console.log("✅ Producto eliminado del servidor");

                await indexedDBService.deleteInventoryItem(op.data.id);
                await indexedDBService.deleteProduct(op.data.productId);
                await indexedDBService.removePendingOperation(op.id);

                successCount++;
              } catch (serverError) {
                console.log(
                  "⚠️ Error eliminando del servidor, eliminando solo de IndexedDB",
                );

                await indexedDBService.deleteInventoryItem(op.data.id);
                await indexedDBService.deleteProduct(op.data.productId);
                await indexedDBService.removePendingOperation(op.id);

                successCount++;
              }
            }
          }
        } catch (error) {
          console.error(`❌ Error procesando operación ${op.id}:`, error);
          errorCount++;

          let errorMessage = "Error desconocido";
          if (error instanceof Error) {
            errorMessage = error.message;
          }

          await indexedDBService.updatePendingOperation(op.id, {
            retryCount: (op.retryCount || 0) + 1,
            lastError: errorMessage,
          });
        }
      }

      // Obtener operaciones restantes DESPUÉS de procesar todas
      const remainingOps = await indexedDBService.getPendingOperations();
      setPendingCount(remainingOps.length);

      // Disparar eventos para actualizar UI
      window.dispatchEvent(new CustomEvent("inventory-changed"));
      window.dispatchEvent(new CustomEvent("products-changed"));
      window.dispatchEvent(new CustomEvent("pending-changed"));

      // Mostrar resultado
      if (errorCount === 0) {
        toast({
          title: "✅ Sincronización completada",
          description: `${successCount} operaciones sincronizadas correctamente`,
          duration: 5000,
        });

        // ============ DISPARAR EVENTO DE SINCRONIZACIÓN COMPLETADA ============
        window.dispatchEvent(new CustomEvent("sync-complete"));
      } else {
        // Mostrar detalles de operaciones que fallaron
        const failedOps = remainingOps.filter((op) => op.retryCount > 0);
        console.error(
          "❌ Operaciones que fallaron:",
          failedOps.map((op) => ({
            id: op.id,
            type: op.type,
            retryCount: op.retryCount,
            lastError: op.lastError,
          })),
        );

        toast({
          title: "⚠️ Sincronización parcial",
          description: `${successCount} exitosas, ${errorCount} fallidas. Los errores de red se reintentarán automáticamente.`,
          variant: "destructive",
          duration: 8000,
        });
      }

      if (onSyncComplete) {
        onSyncComplete();
      }
    } catch (error) {
      console.error("❌ Error en sincronización:", error);
      toast({
        title: "Error de sincronización",
        description: "No se pudieron sincronizar los datos",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2">
        {pendingCount > 0 && (
          <Badge
            variant="outline"
            className="bg-amber-50 text-amber-700 border-amber-200"
          >
            {pendingCount} pendiente{pendingCount !== 1 ? "s" : ""}
          </Badge>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={syncOperations}
              disabled={isSyncing || pendingCount === 0 || !isOnline}
              className="gap-2"
            >
              {isSyncing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">
                {isSyncing ? "Sincronizando..." : "Sincronizar"}
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {!isOnline ? (
              <p>Servidor no disponible</p>
            ) : pendingCount === 0 ? (
              <p>No hay cambios pendientes</p>
            ) : (
              <p>
                Sincronizar {pendingCount} cambio{pendingCount !== 1 ? "s" : ""}{" "}
                pendiente{pendingCount !== 1 ? "s" : ""}
              </p>
            )}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center">
              {isOnline ? (
                <Wifi className="h-4 w-4 text-green-600" />
              ) : (
                <WifiOff className="h-4 w-4 text-red-600" />
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {isOnline ? "Servidor disponible" : "Servidor no disponible"}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
